import { canUseDOM } from '../lib/utils'
import { config } from '../../config/config'
import { listSize } from '../lib/items'
import buildTimestamp from '../../buildtimestamp.json'

/**
 * Request timeout. Client side waits longer than server side. On server side a slow request
 * would hold up the whole SSR response, so we fail fast and let client-side retry the fetch.
 */
const TIMEOUT_MS = canUseDOM ? 30_000 : 8_000


/**
 * Error thrown for failed requests. Extra properties are assigned to the error object.
 * Response status should be assigned to the `status` property, actions check for it.
 */
class ApiError extends Error {
  constructor(message, properties = {}) {
    super(message)
    this.name = 'ApiError'
    Object.assign(this, properties)
  }
}

/**
 * Parse the response body text as JSON and validate it is the timestamped JSON
 * the API is expected to return. If the response was not OK, we throw ApiError.
 * In rare cases 200 responses may invalid, if the request was intercepted by a
 * captive portal or similar. We do not want to let bad data propagate and
 * cause random errors all over the place.
 * @param {Response} response
 * @param {string} text Raw response body text
 * @param {string} [method] HTTP method used for the request
 * @throws {ApiError}
 * @returns {{data: unknown, timestamp: number}} The parsed response body
 */
function parseResponse(response, text, method) {
  if (!response.ok) {
    throw new ApiError(`API request failed with status ${response.status}`, {
      status: response.status,
      url: response.url,
      method,
    })
  }

  let json = undefined
  try {
    json = JSON.parse(text)
  } catch {
    // Ignore here, we'll throw an ApiError below
  }
  const notJSON = !(response.headers.get('content-type')?.startsWith('application/json'))
  const noTimestamp = !(json?.timestamp > 0)
  const noData = !(json?.data)
  if (notJSON || noTimestamp || noData) {
    const dataSample = String(text).replace(/\s+/g, ' ').replace(/> </g, '><').trim()
    throw new ApiError('Unexpected response from API', {
      url: response.url,
      method,
      status: response.status,
      headers: response.headers,
      body: dataSample.length < 150 ? dataSample : (dataSample.slice(0, 140) + '… (truncated)'),
      note: 'This error generally happens when the API call is intercepted by a captive portal or similar, ' +
        'and the response is not what we expect. These are very rare nowadays because of HTTPS, but do ' +
        'still happen in corporate managed devices that have been configured with SSL hijacking/inspection. ' +
        'There is nothing we can do to resolve such issues. We currently log this error to Sentry to keep ' +
        'track of how often it happens. We could maybe reload the page automatically so user can re-authenticate, ' +
        'but we don\'t know how the login flow works, so reloading the page might even be somehow worse. Also ' +
        'afraid of causing reload loops if we ever have bug in our code that causes this error to be thrown.',
    })
  }

  return json
}

/**
 * Build a request URL from a path and optional query parameters, skipping null/undefined values.
 * @param {string} path Path appended to the configured API base URL
 * @param {Record<string, unknown>} [params] Query parameters
 * @returns {string}
 */
function buildApiUrl(path, params) {
  const url = config.apiUrl + path
  if (!params) {
    return url
  }
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      search.append(key, value)
    }
  }
  const query = search.toString()
  return query ? `${url}?${query}` : url
}


/**
 * Perform a fetch request with a timeout, throwing an {@link ApiError} on network errors,
 * timeouts, and non-2xx responses, and validating the response body before returning it.
 * @param {string} method HTTP method
 * @param {string} url Fully qualified request URL
 * @param {{data?: unknown, jwt?: string}} [options]
 * @returns {Promise<unknown>} The validated response body
 */
async function apiRequest(method, url, { data, jwt } = {}) {
  const headers = { Accept: 'application/json' }
  // Manually passing JWT for server-side rendering. SSR does not do any POST/etc requests, so we only
  // set it for GET requests. For client side requests JWT is always automatically passed via cookie.
  if (!canUseDOM && jwt) {
    headers['Authorization'] = `Bearer ${jwt}`
  }
  const hasBody = data != null && method !== 'GET' && method !== 'DELETE'
  if (hasBody) {
    headers['Content-Type'] = 'application/json'
  }
  const controller = new AbortController()
  const fetchOptions = { method, headers, credentials: 'same-origin', signal: controller.signal }
  if (hasBody) {
    fetchOptions.body = JSON.stringify(data)
  }

  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)
  let response, text
  try {
    response = await fetch(url, fetchOptions)
    text = await response.text()
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new ApiError('Request timed out', { url, method, code: 'ERR_TIMEOUT' })
    }
    const message = error?.message ?? 'Network Error'
    const code = error?.code ?? 'ERR_NETWORK'
    throw new ApiError(message, { url, method, code })
  } finally {
    clearTimeout(timeoutId)
  }

  return parseResponse(response, text, method)
}


async function apiGet(path, params, jwt) {
  const url = buildApiUrl(path, params)
  const body = await apiRequest('GET', url, { jwt })
  return body
}

async function apiPost(path, data = {}) {
  const url = buildApiUrl(path)
  const body = await apiRequest('POST', url, { data })
  return body
}

async function apiPut(path, data = {}) {
  const url = buildApiUrl(path)
  const body = await apiRequest('PUT', url, { data })
  return body
}

async function apiDelete(path) {
  const url = buildApiUrl(path)
  const body = await apiRequest('DELETE', url)
  return body
}

/**
 * Fetch the version string of the client currently deployed on server. During SSR we
 * automatically know the version, so we can return it directly without network request.
 * @returns {Promise<string>} Semver string of the client deployed on server
 */
export async function getAppVersion() {
  if (!canUseDOM) {
    return buildTimestamp
  }
  const body = await apiRequest('GET', config.appVersionUrl)
  return body.data.version
}



export function setKeyValue(key, value) {
  return apiPut(`/profile/keyvalue/${key}`, {value})
}


export function getTickers(tickerSlug = null) {
  return apiGet(`/tickers/${tickerSlug ?? ''}`)
}

export function postClick(itemId) {
  return apiPost(`/items/${itemId}/click`)
}

export function postVote(itemId, voteType) {
  return apiPut(`/items/${itemId}/vote`, {vote: voteType})
}

export function postReport(itemId, reportType) {
  return apiPut(`/items/${itemId}/report`, {report: reportType})
}

export function getStatsForItems(item_ids) {
  return apiPost('/items/stats', {item_ids})
}

export function getItem(itemId) {
  return apiGet(`/items/${itemId}`)
}

export function getBatchItems(batchId) {
  return apiGet(`/itemlists/anon/batch/${batchId}`)
}

function getItems(path, jwt, { timestamp, limit = listSize, paywalled, category, tag } = {}) {
  const params = {
    limit: limit,
  }
  if (timestamp) {
    params.max_timestamp = timestamp + 1
  }
  if (paywalled) {
    params.paywalled = paywalled
  }
  if (category) {
    params.category = category
  }
  if (tag) {
    params.tag = tag
  }
  const userType = jwt ? 'user' : 'anon'
  return apiGet(`/itemlists/${userType}/${path}`, params, jwt)
}

export function getNewest(jwt, params) {
  return getItems('newest', jwt, params)
}

export function getUserItems(jwt, params) {
  return getItems('followed', jwt, params)
}

export function getFeatured(jwt, params) {
  return getItems('featured', jwt, params)
}

export function getPaywalled(jwt, params) {
  return getItems('paywalled', jwt, params)
}

export function getMostRead(jwt, params) {
  return getItems('top-read', jwt, params)
}

export function getMostVoted(jwt, params) {
  return getItems('top-voted', jwt, params)
}

export function getItemsForCategory(jwt, categorySlug, params) {
  return getItems(`category/${categorySlug}`, jwt, params)
}

export function getItemsForTag(jwt, tagSlug, params) {
  return getItems(`tag/${tagSlug}`, jwt, params)
}

export function getPopular(jwt, params) {
  return getItems('sidebar-popular', jwt, params)
}

export function getUserFeedItems(jwt, params) {
  return getItems('sidebar-user-feeds', jwt, params)
}

export function getTrending() {
  return apiGet('/trends')
}

export function getBreakingNews(jwt, params) {
  return getItems('breaking', jwt, params)
}

export function searchItems(term, page) {
  const params = {
    q: term,
    limit: listSize,
    page: page || '1',
  }
  return apiGet('/search/', params)
}

export function getCategories() {
  return apiGet('/categories')
}

export function getAllTags() {
  return apiGet('/tags/all')
}
export function getSources() {
  return apiGet('/sources')
}

export function sendAdminEmail(subject, replyto, postData) {
  postData._subject = subject
  postData._replyto = replyto
  return apiPost('/adminemail/', postData)
}


export function setUserVoteFilter(threshold) {
  return apiPut('/profile/vote-filter', { threshold })
}


export function addUserWordFilter(filter) {
  return apiPut(`/profile/word-filters/${encodeURIComponent(filter)}`, null)
}
export function removeUserWordFilter(filter) {
  return apiDelete(`/profile/word-filters/${encodeURIComponent(filter)}`)
}


export function addUserSavedSearch(searchTerm) {
  return apiPut(`/profile/searches/${encodeURIComponent(searchTerm)}`, null)
}
export function removeUserSavedSearch(searchTerm) {
  return apiDelete(`/profile/searches/${encodeURIComponent(searchTerm)}`)
}


export function addUserFeed(url) {
  return apiPost('/profile/feeds/', {url})
}
export function updateUserFeed(feedId, category_id) {
  return apiPut(`/profile/feeds/${feedId}`, {category_id})
}
export function removeUserFeed(feedId) {
  return apiDelete(`/profile/feeds/${feedId}`)
}


export function addWhitelistTag(tagId) {
  return apiPost(`/profile/tags/whitelisted/${tagId}`, null)
}
export function removeWhitelistTag(tagId) {
  return apiDelete(`/profile/tags/whitelisted/${tagId}`)
}
export function addBlacklistTag(tagId) {
  return apiPost(`/profile/tags/blacklisted/${tagId}`, null)
}
export function removeBlacklistTag(tagId) {
  return apiDelete(`/profile/tags/blacklisted/${tagId}`)
}


export function addBlacklistSource(sourceId) {
  return apiPost(`/profile/sources/blacklisted/${sourceId}`, null)
}
export function removeBlacklistSource(sourceId) {
  return apiDelete(`/profile/sources/blacklisted/${sourceId}`)
}


export function addSubscribeSource(sourceId) {
  return apiPost(`/profile/sources/subscriptions/${sourceId}`, null)
}
export function removeSubscribeSource(sourceId) {
  return apiDelete(`/profile/sources/subscriptions/${sourceId}`)
}


export function addWhitelistCategory(categoryId) {
  return apiPost(`/profile/categories/whitelisted/${categoryId}`, null)
}
export function removeWhitelistCategory(categoryId) {
  return apiDelete(`/profile/categories/whitelisted/${categoryId}`)
}
export function addBlacklistCategory(categoryId) {
  return apiPost(`/profile/categories/blacklisted/${categoryId}`, null)
}
export function removeBlacklistCategory(categoryId) {
  return apiDelete(`/profile/categories/blacklisted/${categoryId}`)
}


export function getWeather(params) {
  return apiGet('/weather/', params)
}
export function searchWeatherLocations(searchString) {
  return apiGet('/weather/locations', {query: searchString})
}

export function addWeatherLocation(locationId) {
  return apiPost(`/profile/weather/locations/${locationId}`, null)
}
export function removeWeatherLocation(locationId) {
  return apiDelete(`/profile/weather/locations/${locationId}`)
}
export function setDefaultWeatherLocation(locationId) {
  return apiPut(`/profile/weather/default-location/${locationId}`, null)
}

export function loginOrRegisterWithAlmaTunnus(accessToken) {
  return apiPost('/profile/almalogin', { access_token: accessToken, app_request: false })
}

export function logoutAmpparitUser() {
  return apiPost('/profile/logout')
}

export function getUser(jwt) {
  return apiGet('/profile/', null, jwt)
}
